<template>
  <article class="event-calendar">
    <h3 class="event-calendar__title">
      {{ $t("preparationCalendar.titleInEvent") }}
    </h3>

    <ul class="event-calendar__list">
      <li class="event-calendar__list-item">
        <span
          class="event-calendar__list-item_marker"
          :class="{
            passed: !hasCompleteCompany,
            active: hasCompleteCompany
          }"
        >
          <icon
            v-if="hasCompleteCompany"
            name="done"
            class="event-calendar__list-icon"
          ></icon>
          <icon
            v-else
            name="warning--no-bg"
            class="event-calendar__list-icon"
          ></icon>
        </span>
        <span>
          {{ $t("preparationCalendar.company") }}
        </span>
      </li>

      <li class="event-calendar__list-item" v-if="isShowCatalog">
        <span
          class="event-calendar__list-item_marker"
          :class="{
            passed: !hasOrder,
            active: hasOrder
          }"
        >
          <icon
            v-if="hasOrder"
            name="done"
            class="event-calendar__list-icon"
          ></icon>
          <icon
            v-else
            name="warning--no-bg"
            class="event-calendar__list-icon"
          ></icon>
        </span>
        <span>
          {{
            $t("preparationCalendar.order", {
              date: catalogApplicationDeadline.format("DD MMMM YYYY")
            })
          }}
        </span>
      </li>

      <li class="event-calendar__list-item" v-if="isShowPasses">
        <span
          class="event-calendar__list-item_marker"
          :class="{
            passed: !event.quota?.certificate_count,
            active: event.quota?.certificate_count
          }"
        >
          <icon
            v-if="event.quota?.certificate_count"
            name="done"
            class="event-calendar__list-icon"
          ></icon>
          <icon
            v-else
            name="warning--no-bg"
            class="event-calendar__list-icon"
          ></icon>
        </span>
        <span>
          {{
            $t("preparationCalendar.pass", {
              date: passesApplicationDeadline.format("DD MMMM YYYY")
            })
          }}
        </span>
      </li>
      <li v-if="isShowAddServices" class="event-calendar__list-item">
        <span
          class="event-calendar__list-item_marker"
          :class="{
            passed: !ordersCount,
            active: ordersCount
          }"
        >
          <icon
            v-if="ordersCount"
            name="done"
            class="event-calendar__list-icon"
          ></icon>
          <icon
            v-else
            name="warning--no-bg"
            class="event-calendar__list-icon"
          ></icon>
        </span>
        <span>
          {{
            $t("preparationCalendar.addServices", {
              date: additionalOrderDeadline.format("DD MMMM YYYY")
            })
          }}
        </span>
      </li>
    </ul>
  </article>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import moment from "moment";
import { i18n } from "@v2/shared/config";

export default {
  name: "EventCalendar",
  computed: {
    ...mapState("event", ["event"]),
    ...mapState("eventCatalog", {
      info: "info"
    }),
    ...mapGetters("event", ["hasCompleteCompany", "hasOrder", "passDeadline"]),
    ordersCount() {
      return this.event.additional_orders_count || 0;
    },
    catalogApplicationDeadline() {
      return moment(this.info.catalog_order_ended_at).locale(i18n.locale);
    },

    passesApplicationDeadline() {
      const passDeadline = {
        certificate: this.event.exhibition.personal_pass_ended_at,
        installation: this.event.exhibition.installer_pass_ended_at
      };

      if (this.event.type === "installer") {
        return moment(passDeadline.installation).locale(i18n.locale);
      }

      if (this.event.type !== "installer" && this.event.is_self_builder) {
        const closestDeadline =
          moment(this.event.exhibition.personal_pass_ended_at) >
          moment(this.event.exhibition.installer_pass_ended_at)
            ? this.event.exhibition.installer_pass_ended_at
            : this.event.exhibition.personal_pass_ended_at;

        return moment(closestDeadline).locale(i18n.locale);
      }
      return moment(passDeadline.certificate).locale(i18n.locale);
    },
    additionalOrderDeadline() {
      return moment(this.event.exhibition.additional_order_deadline);
    },
    isShowCatalog() {
      return (
        this.event.catalog_template_id &&
        this.event.type !== "special-project" &&
        this.event.type !== "organizer-and-media"
      );
    },
    isShowAddServices() {
      return (
        this.event.is_show_order_additional_services &&
        this.event.type !== "special-project" &&
        this.event.type !== "organizer-and-media" &&
        this.event.type !== "part-time"
      );
    },
    isShowPasses() {
      return this.event.type !== "part-time";
    }
  }
};
</script>

<style lang="scss">
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.event-calendar {
  box-sizing: border-box;
  padding: 29px 60px 43px 39px;

  @include respond-down(lg) {
    width: 350px;
    padding: 24px 28px;
  }

  @include respond-down(md) {
    padding: 20px 15px 26px 24px;
  }

  &__title {
    @include h2();
    letter-spacing: 0.1px;

    @include respond-down(md) {
      font-size: 16px;
    }
  }

  &__list {
    list-style: none;

    margin: 36px 0 0 1px;
    padding: 0;
    max-width: 350px;
    @include respond-down(md) {
      max-width: none;
      margin: 16px 0 0 1px;
    }
    &-icon {
      width: 16px;
      height: 16px;
    }
    &-item {
      display: flex;
      align-items: center;
      font-size: 14px;
      font-weight: normal;
      font-style: normal;
      font-stretch: normal;
      line-height: 1.43;
      letter-spacing: normal;
      color: #000;

      @include respond-down(md) {
        font-size: 12px;
      }

      &:not(:last-child) {
        margin-bottom: 16px;
      }

      &_marker {
        width: 32px;
        height: 32px;
        border-radius: 20px;

        flex-shrink: 0;

        margin-right: 16.2px;

        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        position: relative;

        &.active {
          background-color: #005197;
          border: 1px solid #005197;
        }

        &.passed {
          border: 1px solid #a3a8b3;
          background-color: transparent;
        }
      }
    }
  }
}
</style>
